import React, { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SecurityFlowDataContext } from "../../SecurityTestingPage";
import RequirementsPhase from "../../../input-components/security-testing/RequirementsPhase";
import IdentifiedFunctionalModules from "../../../output-components/security-testing/IdentifiedFunctionalModules";
import { Box, Container, Typography } from "@mui/material";
import { ReactComponent as AutomateIcon } from "../../../../images/automateIcon.svg";
import FullscreenToggle from "../../../custom-components/FullScreenToggle";

const RequirementOutputPage = () => {
  const location = useLocation();
  const {
    brd,
    setBRD,
    userStory,
    setUserStory,
    workFlow,
    setWorkFlow,
    funcMod,
    setFuncMod,
    securityReq,
    setSecurityReq,
    archDesign,
    setArchDesign,
  } = useContext(SecurityFlowDataContext);

  useEffect(() => {

    // Check if any input file exists
    const hasNavigationData = !!location.state;

    if (!hasNavigationData) {
      return;
    }


    // If location.state provides fresh values → use them
    if (location.state?.brd !== undefined) {
      setBRD(location.state.brd);
      localStorage.setItem("brd", JSON.stringify(location.state.brd));
    }
    if (location.state?.userStory !== undefined) {
      setUserStory(location.state.userStory);
      localStorage.setItem("userStory", JSON.stringify(location.state.userStory));
    }
    if (location.state?.workFlow !== undefined) {
      setWorkFlow(location.state.workFlow);
      localStorage.setItem("workFlow", JSON.stringify(location.state.workFlow));
    }
    if (location.state?.funcMod !== undefined) {
      setFuncMod(location.state.funcMod);
      localStorage.setItem("funcMod", JSON.stringify(location.state.funcMod));
    }
    if (location.state?.securityReq !== undefined) {
      setSecurityReq(location.state.securityReq);
      localStorage.setItem("securityReq", JSON.stringify(location.state.securityReq));
    }
    if (location.state?.archDesign) {
      setArchDesign(location.state.archDesign);
      localStorage.setItem("archDesign", JSON.stringify(location.state.archDesign));
    }

    // If nothing in location.state, restore from localStorage
    if (!location.state) {
      const storedFuncMod = localStorage.getItem("funcMod");
      if (storedFuncMod) setFuncMod(JSON.parse(storedFuncMod));

      const storedSecurityReq = localStorage.getItem("securityReq");
      if (storedSecurityReq) setSecurityReq(JSON.parse(storedSecurityReq));

      const storedArchDesign = localStorage.getItem("archDesign");
      if (storedArchDesign) setArchDesign(JSON.parse(storedArchDesign));

      const storedBRD = localStorage.getItem("brd");
      if (storedBRD) setBRD(JSON.parse(storedBRD));

      const storedUserStory = localStorage.getItem("userStory");
      if (storedUserStory) setUserStory(JSON.parse(storedUserStory));

      const storedWorkFlow = localStorage.getItem("workFlow");
      if (storedWorkFlow) setWorkFlow(JSON.parse(storedWorkFlow));
    }
  }, [
    location.state,
    setBRD,
    setUserStory,
    setWorkFlow,
    setFuncMod,
    setSecurityReq,
    setArchDesign,
  ]);

  // 👇 Keep outputs synced in localStorage
  useEffect(() => {


    if (funcMod) localStorage.setItem("funcMod", JSON.stringify(funcMod));
    else localStorage.removeItem("funcMod");

    if (securityReq) localStorage.setItem("securityReq", JSON.stringify(securityReq));
    else localStorage.removeItem("securityReq");

    if (archDesign) localStorage.setItem("archDesign", JSON.stringify(archDesign));
    else localStorage.removeItem("archDesign");

    if (brd) localStorage.setItem("brd", JSON.stringify(brd));
    else localStorage.removeItem("brd");

    if (userStory) localStorage.setItem("userStory", JSON.stringify(userStory));
    else localStorage.removeItem("userStory");

    if (workFlow) localStorage.setItem("workFlow", JSON.stringify(workFlow));
    else localStorage.removeItem("workFlow");
  }, [funcMod, securityReq, archDesign, brd, userStory, workFlow]);


  const hasInitialInput = useMemo(() => {
    return (
      (brd && Object.keys(brd).length > 0) ||
      (userStory && Object.keys(userStory).length > 0) ||
      (workFlow && Object.keys(workFlow).length > 0)
    );
  }, [brd, userStory, workFlow]);

  const hasGeneratedOutputs = useMemo(() => {
    const hasFuncMod =
      typeof funcMod === "object" &&
      funcMod !== null &&
      Object.keys(funcMod).length > 0;
    const hasSecurityReq =
      typeof securityReq === "object" &&
      securityReq !== null &&
      Object.keys(securityReq).length > 0;
    const hasArchDesign =
      typeof archDesign === "object" &&
      archDesign !== null &&
      Object.keys(archDesign).length > 0;
    return hasFuncMod && hasSecurityReq && hasArchDesign;
  }, [funcMod, securityReq, archDesign]);



  console.log("=== Requirement Output ===");
  console.log("location.state:", location.state);

  console.log("funcMod:", funcMod);
  console.log("securityReq:", securityReq);
  console.log("archDesign:", archDesign);

  console.log("hasGeneratedOutputs:", hasGeneratedOutputs);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
        bgcolor: "#f6f3f2",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
        px: { xs: 2, sm: 3, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          "& > *:not(:last-child)": {
            mb: 2,
          },
          mb: 6,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center">
            <AutomateIcon style={{ marginRight: 4 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ mt: 1 }}
            >
              Generate fast and automated security checks
            </Typography>
          </Box>
          <FullscreenToggle />
        </Box>

        {<RequirementsPhase />}
        {hasGeneratedOutputs && <IdentifiedFunctionalModules />}
      </Container>

      {/* Sticky footer */}
      <Box
        component="footer"
        sx={{
          width: "100%",
          backgroundColor: "#f6f3f2",
          textAlign: "center",
          px: 2,
          py: 1,
          position: "fixed",
          bottom: 0,
          zIndex: 99,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Use QMentisAI™ with human supervision, as with all generative AI
          tools.
        </Typography>
      </Box>
    </Box>
  );
};

export default RequirementOutputPage;
